import { useState, useEffect, useCallback, useRef } from "react";
import * as XLSX from "xlsx";
import YogaCalendar from "../../components/YogaCalendar";
import { staff } from "../../data";
import "./admin.css";

const API          = "https://iy-sp.org/api";
const ADMIN_SECRET = "admin123";
const AUTH         = { "Content-Type": "application/json", "Authorization": `Bearer ${ADMIN_SECRET}` };

const PASS_TYPES = ["pass4", "pass8", "member2x", "memberUnl", "private1", "private3", "private10"];
const PASS_LABELS = {
  dropin:    "Drop-in",
  pass4:     "4-Class Pass",
  pass8:     "8-Class Pass",
  member2x:  "2x/week Membership",
  memberUnl: "Unlimited Membership",
  private1:  "Private — 1 Class",
  private3:  "Private — 3 Classes",
  private10: "Private — 10 Classes",
};

// Classes are loaded dynamically from the backend — see useClasses() hook below
const CLASSES_FALLBACK = [
  { id: "cls_wed", label: "Wed 6pm — Iyengar Yoga" },
  { id: "cls_fri", label: "Fri 10am — Iyengar Yoga" },
  { id: "cls_sat", label: "Sat 8:15am — Iyengar Yoga (Online)" },
  { id: "cls_sun", label: "Sun 10am — Iyengar Yoga (Online)" },
];



// Hook — fetches all classes from MongoDB and formats them for dropdowns
function useClasses() {
  const [classes, setClasses] = useState(CLASSES_FALLBACK);
  useEffect(() => {
    fetch(`${API}/schedule/classes`)
      .then(r => r.json())
      .then(data => {
        const loaded = (data.classes ?? []).map(c => {
          const days  = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];
          const day   = days[c.dayOfWeek] ?? "";
          const [h, m] = (c.time ?? "00:00").split(":").map(Number);
          const ampm  = h >= 12 ? "pm" : "am";
          const hour  = h % 12 || 12;
          const time  = `${hour}:${String(m).padStart(2,"0")}${ampm}`;
          const venue = c.venue === "venue_online" ? " (Online)" : "";
          return { id: c.id, label: `${day} ${time}${venue} — ${c.title}` };
        });
        if (loaded.length > 0) setClasses(loaded);
      })
      .catch(() => {}); // silently fall back to CLASSES_FALLBACK
  }, []);
  return classes;
}

const STAFF_MAP = Object.fromEntries(staff.map(s => [s.name, s.payRate]));

function toLocalDateStr(date) {
  return [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, "0"),
    String(date.getDate()).padStart(2, "0"),
  ].join("-");
}

function fmtDate(dateStr) {
  if (!dateStr) return "—";
  const d = new Date(typeof dateStr === "string" ? dateStr + (dateStr.length === 10 ? "T12:00:00" : "") : dateStr);
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function fmtMoney(n) {
  return "$" + (n ?? 0).toFixed(2);
}

function currentMonth() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

// ── XLSX export helper ────────────────────────────────────
function exportXLSX(sheetName, rows, filename) {
  const ws = XLSX.utils.json_to_sheet(rows);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, sheetName);
  XLSX.writeFile(wb, filename);
}

// ── Password Gate ─────────────────────────────────────────
function PasswordGate({ onSuccess }) {
  const [pw, setPw]       = useState("");
  const [error, setError] = useState(false);
  const submit = () => {
    if (pw === ADMIN_SECRET) onSuccess();
    else { setError(true); setPw(""); }
  };
  return (
    <div className="adm-gate">
      <div className="adm-gate-card">
        <div className="adm-gate-logo">⚙</div>
        <h1 className="adm-gate-title">Admin Dashboard</h1>
        <p className="adm-gate-sub">Iyengar Yoga St. Petersburg</p>
        <input autoFocus type="password"
          className={`adm-gate-input ${error ? "input-error" : ""}`}
          placeholder="Password" value={pw}
          onChange={e => { setPw(e.target.value); setError(false); }}
          onKeyDown={e => e.key === "Enter" && submit()}
        />
        {error && <p className="adm-gate-error">Incorrect password.</p>}
        <button className="adm-btn-primary" onClick={submit}>Enter</button>
      </div>
    </div>
  );
}

// ── Reschedule Modal ──────────────────────────────────────
function RescheduleModal({ booking, onDone, onClose }) {
  const CLASSES = useClasses();
  const [newClassId, setNewClassId] = useState(booking.classId);
  const [newDate, setNewDate]       = useState(booking.date);
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState(null);

  const handleReschedule = async () => {
    if (!newClassId || !newDate) { setError("Please select a class and date."); return; }
    if (newClassId === booking.classId && newDate === booking.date) {
      setError("Please choose a different class or date."); return;
    }
    setLoading(true); setError(null);
    try {
      const res  = await fetch(`${API}/bookings/${booking._id}/reschedule`, {
        method: "PATCH", headers: AUTH,
        body: JSON.stringify({ newClassId, newDate }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error ?? "Could not reschedule."); return; }
      onDone();
    } catch { setError("Something went wrong."); }
    finally   { setLoading(false); }
  };

  return (
    <div className="adm-modal-overlay" onClick={onClose}>
      <div className="adm-modal" onClick={e => e.stopPropagation()}>
        <button className="adm-modal-close" onClick={onClose}>✕</button>
        <h2 className="adm-modal-title">Reschedule Booking</h2>
        <p className="adm-modal-sub">
          Student: <strong>{booking.studentName}</strong><br />
          Currently: <strong>{CLASSES.find(c => c.id === booking.classId)?.label ?? booking.classId}</strong> on <strong>{booking.date}</strong>
        </p>
        <label className="adm-modal-label">New class</label>
        <select className="adm-input adm-modal-input" value={newClassId} onChange={e => setNewClassId(e.target.value)}>
          {CLASSES.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
        </select>
        <label className="adm-modal-label">New date</label>
        <input type="date" className="adm-input adm-modal-input" value={newDate} onChange={e => setNewDate(e.target.value)} />
        {error && <p className="adm-error">{error}</p>}
        <div className="adm-modal-actions">
          <button className="adm-btn-ghost-sm" onClick={onClose}>Cancel</button>
          <button className="adm-btn-primary" onClick={handleReschedule} disabled={loading}>
            {loading ? "Rescheduling…" : "Confirm reschedule"}
          </button>
        </div>
      </div>
    </div>
  );
}



// ── Book Private Modal ────────────────────────────────────
function BookPrivateModal({ onClose, onDone }) {
  const CLASSES   = useClasses();
  const [step, setStep]       = useState("form");
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState("");
  const [resultMsg, setResultMsg] = useState("");
  const [form, setForm] = useState({ name: "", email: "", className: "Private 1-to-1", date: "", time: "", duration: "60" });
  const privateClasses = CLASSES.filter(c => c.isPrivate);

  const handleChange = e => { setForm(p => ({ ...p, [e.target.name]: e.target.value })); setError(""); };

  const handleSubmit = async () => {
    const { name, email, className, date, time, duration } = form;
    if (!name || !email || !className || !date || !time) { setError("Please fill in all fields."); return; }
    setLoading(true); setError("");
    try {
      const res  = await fetch(`${API}/bookings/private`, {
        method: "POST", headers: AUTH,
        body: JSON.stringify({ name, email, className, date, time, duration: Number(duration) }),
      });
      const data = await res.json();
      if (res.status === 201) {
        setResultMsg(`Booked. ${data.classesRemaining} class${data.classesRemaining !== 1 ? "es" : ""} remaining on pass.`);
        setStep("success"); onDone?.();
      } else if (res.status === 402 && data.needsPass) {
        setStep("needsPass");
      } else {
        setError(data.error || data.message || "Something went wrong.");
      }
    } catch { setError("Network error."); }
    finally   { setLoading(false); }
  };

  return (
    <div className="adm-modal-overlay" onClick={onClose}>
      <div className="adm-modal adm-modal--private" onClick={e => e.stopPropagation()}>
        <div className="adm-modal-header">
          <h3 className="adm-modal-title">Book Private Class</h3>
          <button className="adm-modal-close" onClick={onClose}>✕</button>
        </div>
        {step === "form" && (
          <div className="adm-modal-body">
            {["name","email"].map(field => (
              <div key={field} className="adm-field-group">
                <label className="adm-label">{field === "name" ? "Student name" : "Student email"}</label>
                <input className="adm-input" name={field} type={field === "email" ? "email" : "text"}
                  value={form[field]} onChange={handleChange}
                  placeholder={field === "name" ? "Full name" : "student@example.com"} />
              </div>
            ))}
            <div className="adm-field-group">
              <label className="adm-label">Class</label>
              {privateClasses.length > 0
                ? <select className="adm-input" name="className" value={form.className} onChange={handleChange}>
                    {privateClasses.map(c => <option key={c.id || c._id} value={c.title}>{c.title}</option>)}
                  </select>
                : <input className="adm-input" name="className" value={form.className} onChange={handleChange} placeholder="e.g. Private 1-to-1" />
              }
            </div>
            <div className="adm-field-row">
              <div className="adm-field-group">
                <label className="adm-label">Date</label>
                <input className="adm-input" name="date" type="date"
                  min={new Date().toISOString().slice(0, 10)} value={form.date} onChange={handleChange} />
              </div>
              <div className="adm-field-group">
                <label className="adm-label">Time</label>
                <input className="adm-input" name="time" type="time" value={form.time} onChange={handleChange} />
              </div>
            </div>
            <div className="adm-field-group">
              <label className="adm-label">Duration</label>
              <select className="adm-input" name="duration" value={form.duration} onChange={handleChange}>
                <option value="60">1 hour</option>
                <option value="90">1.5 hours</option>
              </select>
            </div>
            {error && <p className="adm-error">{error}</p>}
            <div className="adm-modal-actions">
              <button className="adm-btn adm-btn--ghost" onClick={onClose} disabled={loading}>Cancel</button>
              <button className="adm-btn adm-btn--primary" onClick={handleSubmit} disabled={loading}>
                {loading ? "Booking…" : "Book Class"}
              </button>
            </div>
          </div>
        )}
        {step === "success" && (
          <div className="adm-modal-body adm-modal-result adm-modal-result--success">
            <span className="adm-result-icon">✓</span>
            <p className="adm-result-title">Private class booked</p>
            <p className="adm-result-sub">{resultMsg}</p>
            <button className="adm-btn adm-btn--primary" onClick={onClose}>Done</button>
          </div>
        )}
        {step === "needsPass" && (
          <div className="adm-modal-body adm-modal-result adm-modal-result--warn">
            <span className="adm-result-icon">✉</span>
            <p className="adm-result-title">No active private pass</p>
            <p className="adm-result-sub">Pricing email sent to <strong>{form.email}</strong>.</p>
            <button className="adm-btn adm-btn--primary" onClick={onClose}>OK</button>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Bookings Tab ──────────────────────────────────────────
function BookingsTab() {
  const CLASSES = useClasses();
  const [date, setDate]                   = useState(toLocalDateStr(new Date()));
  const [classId, setClassId]             = useState("cls_wed");
  const [bookings, setBookings]           = useState([]);
  const [confirmedCount, setConfirmedCount] = useState(0);
  const [loading, setLoading]             = useState(false);
  const [error, setError]                 = useState(null);
  const [rescheduleTarget, setRescheduleTarget] = useState(null);
  const [showPrivateModal, setShowPrivateModal] = useState(false);
  const [cancelClassResult, setCancelClassResult] = useState(null);
  const [cancellingClass, setCancellingClass]     = useState(false);
  const [showAddStudent, setShowAddStudent] = useState(false);
  const [addStudentForm, setAddStudentForm] = useState({ name: "", email: "", paymentType: "", passCode: "" });
  const [addStudentError, setAddStudentError] = useState("");
  const [addStudentLoading, setAddStudentLoading] = useState(false);

  const fetchBookings = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res  = await fetch(`${API}/bookings?classId=${classId}&date=${date}`, { headers: AUTH });
      const data = await res.json();
      setBookings(data.bookings ?? []);
      setConfirmedCount(data.confirmedCount ?? 0);
    } catch { setError("Could not load bookings."); }
    finally   { setLoading(false); }
  }, [classId, date]);

  useEffect(() => { fetchBookings(); }, [fetchBookings]);

  const handleCancel = async id => {
    if (!window.confirm("Cancel this booking and restore pass credit?")) return;
    await fetch(`${API}/bookings/${id}/cancel`, { method: "PATCH", headers: AUTH });
    fetchBookings();
  };

  const handleCheckin = async (id, checked) => {
    await fetch(`${API}/reports/checkin/${id}`, {
      method: "PATCH", headers: AUTH,
      body: JSON.stringify({ checkedIn: checked }),
    });
    fetchBookings();
  };

  async function handleAddStudent(e) {
  e.preventDefault();
  setAddStudentError("");
  setAddStudentLoading(true);
  try {
    const res = await fetch(`${API}/bookings/admin-add`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${ADMIN_SECRET}` },
      body: JSON.stringify({
        studentName: addStudentForm.name,
        studentEmail: addStudentForm.email,
        classId: classId,
        date: date,
        paymentType: addStudentForm.paymentType,
        passCode: addStudentForm.passCode || undefined,
      }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || "Failed to add student.");
    setShowAddStudent(false);
    setAddStudentForm({ name: "", email: "", paymentType: "dropin", passCode: "" });
    fetchBookings(); // refresh roster
  } catch (err) {
    setAddStudentError(err.message);
  } finally {
    setAddStudentLoading(false);
  }
}

  const handleCancelClass = async () => {
    const cls = CLASSES.find(c => c.id === classId);
    if (!window.confirm(`Cancel ALL confirmed bookings for ${cls?.label} on ${date}?`)) return;
    setCancellingClass(true); setCancelClassResult(null);
    try {
      const res  = await fetch(`${API}/bookings/cancel-class`, {
        method: "PATCH", headers: AUTH,
        body: JSON.stringify({ classId, date }),
      });
      const data = await res.json();
      setCancelClassResult(res.ok ? { affected: data.affected, results: data.results } : { error: data.error ?? "Could not cancel." });
      if (res.ok) fetchBookings();
    } catch { setCancelClassResult({ error: "Network error." }); }
    finally   { setCancellingClass(false); }
  };

  const cls = CLASSES.find(c => c.id === classId);

  return (
    <div className="adm-tab-content">
      <div className="adm-filters">
        <div className="adm-filter-group">
          <label className="adm-filter-label">Date</label>
          <input type="date" className="adm-input" value={date} onChange={e => setDate(e.target.value)} />
        </div>
        <div className="adm-filter-group">
          <label className="adm-filter-label">Class</label>
          <select className="adm-input" value={classId} onChange={e => setClassId(e.target.value)}>
            {CLASSES.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
          </select>
        </div>
        <button className="adm-btn adm-btn--primary adm-btn--book-private" onClick={() => setShowPrivateModal(true)}>
          + Book Private
        </button>
      </div>

      <div className="adm-roster-header">
        <h2 className="adm-section-title">{cls?.label}</h2>
        <div className="adm-roster-header-actions">
          <span className="adm-count-badge">{confirmedCount} booked</span>
            <button className="adm-btn-sm" onClick={() => setShowAddStudent(true)}>+ Add Student</button> 
          {confirmedCount > 0 && (
            <button className="adm-btn-danger-sm" onClick={handleCancelClass} disabled={cancellingClass}>
              {cancellingClass ? "Cancelling…" : "Cancel This Class"}
            </button>
          )}
        </div>
      </div>

      {cancelClassResult && !cancelClassResult.error && (
        <div className="adm-cancel-class-result">
          <strong>{cancelClassResult.affected} booking{cancelClassResult.affected !== 1 ? "s" : ""} cancelled.</strong>
          {cancelClassResult.results?.some(r => r.creditCode) && (
            <ul className="adm-cancel-credits">
              {cancelClassResult.results.filter(r => r.creditCode).map(r => (
                <li key={r.email}>{r.name} — credit: <code>{r.creditCode}</code></li>
              ))}
            </ul>
          )}
          <button className="adm-btn-ghost-sm" onClick={() => setCancelClassResult(null)}>Dismiss</button>
        </div>
      )}
      {cancelClassResult?.error && <p className="adm-error">{cancelClassResult.error}</p>}

      {loading && <p className="adm-loading">Loading…</p>}
      {error   && <p className="adm-error">{error}</p>}

      {!loading && bookings.length > 0 && (
        <div className="adm-table-wrap">
          <table className="adm-table">
            <thead>
              <tr>
                <th>Name</th><th>Email</th><th>Payment</th><th>Status</th>
                <th style={{textAlign:"center"}}>Checked In</th><th></th>
              </tr>
            </thead>
            <tbody>
              {bookings.map(b => (
                <tr key={b._id} className={b.status === "cancelled" ? "row-cancelled" : ""}>
                  <td>{b.studentName}</td>
                  <td className="adm-email">{b.studentEmail}</td>
                  <td><span className="adm-type-badge">{PASS_LABELS[b.paymentType] ?? b.paymentType}</span></td>
                  <td><span className={`adm-status-badge status-${b.status}`}>{b.status}</span></td>
                  <td style={{textAlign:"center"}}>
                    {b.status === "confirmed" && (
                      <button
                        className={`adm-checkin-btn ${b.checkedIn ? "checked" : ""}`}
                        onClick={() => handleCheckin(b._id, !b.checkedIn)}
                        title={b.checkedIn ? "Mark absent" : "Mark attended"}
                      >
                        {b.checkedIn ? "✓" : "○"}
                      </button>
                    )}
                  </td>
                  <td>
                    {b.status === "confirmed" && (
                      <div className="adm-row-actions">
                        <button className="adm-btn-ghost-sm" onClick={() => setRescheduleTarget(b)}>Reschedule</button>
                        <button className="adm-btn-danger-sm" onClick={() => handleCancel(b._id)}>Cancel</button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {!loading && bookings.length === 0 && !error && (
        <p className="adm-empty">No bookings for this class and date.</p>
      )}

      {rescheduleTarget && (
        <RescheduleModal booking={rescheduleTarget} onDone={() => { setRescheduleTarget(null); fetchBookings(); }} onClose={() => setRescheduleTarget(null)} />
      )}
      {showPrivateModal && (
        <BookPrivateModal onClose={() => setShowPrivateModal(false)} onDone={() => setShowPrivateModal(false)} />
      )}
       {showAddStudent && (
        <div className="adm-modal-overlay" onClick={() => setShowAddStudent(false)}>
          <div className="adm-modal" onClick={e => e.stopPropagation()}>
            <div className="adm-modal-header">
              <h3 className="adm-modal-title">Add Student to Class</h3>
              <button className="adm-modal-close" onClick={() => setShowAddStudent(false)}>✕</button>
            </div>
            <div className="adm-modal-body">
              <form onSubmit={handleAddStudent}>
                <div className="adm-field-group">
                  <label className="adm-label">Name</label>
                  <input className="adm-input" value={addStudentForm.name} onChange={e => setAddStudentForm(f => ({ ...f, name: e.target.value }))} required />
                </div>
                <div className="adm-field-group">
                  <label className="adm-label">Email</label>
                  <input className="adm-input" type="email" value={addStudentForm.email} onChange={e => setAddStudentForm(f => ({ ...f, email: e.target.value }))} required />
                </div>
                <div className="adm-field-group">
                  <label className="adm-label">Payment Type</label>
                  <select className="adm-input" value={addStudentForm.paymentType} onChange={e => setAddStudentForm(f => ({ ...f, paymentType: e.target.value }))}>
                    <option value="dropin">Drop-in</option>
                    <option value="pass4">4-Class Pass</option>
                    <option value="pass8">8-Class Pass</option>
                    <option value="member2x">Member 2x</option>
                    <option value="memberUnl">Member Unlimited</option>
                  </select>
                </div>
                {addStudentForm.paymentType !== "dropin" && (
                  <div className="adm-field-group">
                    <label className="adm-label">Pass Code <span style={{fontWeight:400,fontSize:"0.85em"}}>(optional — e.g. YOGA-A3F2)</span></label>
                    <input className="adm-input" value={addStudentForm.passCode} onChange={e => setAddStudentForm(f => ({ ...f, passCode: e.target.value }))} placeholder="YOGA-XXXX" />
                  </div>
                )}
                {addStudentError && <p className="adm-error">{addStudentError}</p>}
                <div className="adm-modal-actions">
                  <button type="button" className="adm-btn adm-btn--ghost" onClick={() => setShowAddStudent(false)}>Cancel</button>
                  <button type="submit" className="adm-btn adm-btn--primary" disabled={addStudentLoading}>
                    {addStudentLoading ? "Adding…" : "Add Student"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

// ── Students Tab ──────────────────────────────────────────
function StudentsTab() {
  const [students, setStudents]   = useState([]);
  const [search, setSearch]       = useState("");
  const [loading, setLoading]     = useState(true);
  const [selected, setSelected]   = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  useEffect(() => {
    fetch(`${API}/students`, { headers: AUTH })
      .then(r => r.json()).then(d => setStudents(d.students ?? []))
      .catch(() => {}).finally(() => setLoading(false));
  }, []);

  const loadStudent = async email => {
    setDetailLoading(true);
    try {
      const res  = await fetch(`${API}/students/${email}`, { headers: AUTH });
      const data = await res.json();
      setSelected(data);
    } catch { setSelected(null); }
    finally   { setDetailLoading(false); }
  };

  const filtered = students.filter(s =>
    s.name?.toLowerCase().includes(search.toLowerCase()) ||
    s.email?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="adm-tab-content adm-two-col">
      <div className="adm-col-list">
        <input className="adm-input adm-search" placeholder="Search name or email…"
          value={search} onChange={e => setSearch(e.target.value)} />
        {loading && <p className="adm-loading">Loading…</p>}
        <div className="adm-student-list">
          {filtered.map(s => (
            <button key={s._id}
              className={`adm-student-row ${selected?.student?._id === s._id ? "active" : ""}`}
              onClick={() => loadStudent(s.email)}>
              <span className="adm-student-name">{s.name}</span>
              <span className="adm-student-email">{s.email}</span>
            </button>
          ))}
          {!loading && filtered.length === 0 && <p className="adm-empty">No students found.</p>}
        </div>
      </div>

      <div className="adm-col-detail">
        {!selected && !detailLoading && <div className="adm-detail-placeholder"><span>Select a student to view details</span></div>}
        {detailLoading && <p className="adm-loading">Loading…</p>}
        {selected && !detailLoading && (
          <>
            <div className="adm-detail-header">
              <h2 className="adm-detail-name">{selected.student.name}</h2>
              <p className="adm-detail-email">{selected.student.email}</p>
              {selected.student.city && (
                <p className="adm-detail-email">
                  {[selected.student.city, selected.student.state, selected.student.zip].filter(Boolean).join(", ")}
                  {selected.student.county && <span className="adm-locality-badge">{selected.student.county} Co.</span>}
                </p>
              )}
              {selected.student.notes && <p className="adm-detail-notes">{selected.student.notes}</p>}
            </div>

            <h3 className="adm-detail-section">Passes</h3>
            {selected.passes?.length === 0 && <p className="adm-empty">No passes.</p>}
            {selected.passes?.map(p => (
              <div key={p._id} className={`adm-pass-card ${!p.active ? "pass-inactive" : ""}`}>
                <div className="adm-pass-top">
                  <span className="adm-pass-code">{p.code}</span>
                  <span className={`adm-pass-status ${p.active ? "pass-active" : "pass-used"}`}>{p.active ? "Active" : "Inactive"}</span>
                </div>
                <div className="adm-pass-meta">
                  <span>{PASS_LABELS[p.type] ?? p.type}</span>
                  {p.classesTotal != null && <span>{p.classesTotal - p.classesUsed} of {p.classesTotal} remaining</span>}
                  {p.expiresAt && <span>Expires {new Date(p.expiresAt).toLocaleDateString()}</span>}
                </div>
              </div>
            ))}

            <h3 className="adm-detail-section">Recent Bookings</h3>
            {selected.bookings?.length === 0 && <p className="adm-empty">No bookings.</p>}
            {selected.bookings?.slice(0, 10).map(b => (
              <div key={b._id} className={`adm-booking-row ${b.status === "cancelled" ? "booking-cancelled" : ""}`}>
                <span className="adm-booking-date">{b.date}</span>
                <span className="adm-booking-class">{b.classId}</span>
                <span className={`adm-status-badge status-${b.status}`}>{b.status}</span>
                {b.checkedIn && <span className="adm-checkedin-tag">✓ attended</span>}
              </div>
            ))}
          </>
        )}
      </div>
    </div>
  );
}

// ── Passes Tab ────────────────────────────────────────────
function PassesTab() {
  const [passes, setPasses]     = useState([]);
  const [loading, setLoading]   = useState(true);
  const [search, setSearch]     = useState("");
  const [showForm, setShowForm] = useState(false);
  const [form, setForm]         = useState({ type: "pass4", studentEmail: "", classesTotal: 4, expiresAt: "", notes: "" });
  const [saving, setSaving]     = useState(false);
  const [formError, setFormError] = useState(null);

  const fetchPasses = useCallback(async () => {
    setLoading(true);
    try {
      const res  = await fetch(`${API}/passes`, { headers: AUTH });
      const data = await res.json();
      setPasses(data.passes ?? []);
    } catch { setPasses([]); }
    finally   { setLoading(false); }
  }, []);

  useEffect(() => { fetchPasses(); }, [fetchPasses]);

  const handleCreate = async () => {
    if (!form.studentEmail.trim()) { setFormError("Email is required."); return; }
    setSaving(true); setFormError(null);
    try {
      const classesByType = { pass4: 4, pass8: 8, private1: 1, private3: 3, private10: 10 };
      const body = {
        type: form.type, studentEmail: form.studentEmail.trim(),
        classesTotal: ["member2x","memberUnl"].includes(form.type) ? null : classesByType[form.type] ?? Number(form.classesTotal),
        expiresAt: form.expiresAt || null, notes: form.notes || "",
      };
      const res = await fetch(`${API}/passes`, { method: "POST", headers: AUTH, body: JSON.stringify(body) });
      if (!res.ok) { const d = await res.json(); setFormError(d.error ?? "Failed."); return; }
      setShowForm(false);
      setForm({ type: "pass4", studentEmail: "", classesTotal: 4, expiresAt: "", notes: "" });
      fetchPasses();
    } catch { setFormError("Something went wrong."); }
    finally   { setSaving(false); }
  };

  const toggleActive = async (id, active) => {
    if (active && !window.confirm("Deactivate this pass?")) return;
    await fetch(`${API}/passes/${id}`, { method: "PATCH", headers: AUTH, body: JSON.stringify({ active: !active }) });
    fetchPasses();
  };

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const isMembership = ["member2x","memberUnl"].includes(form.type);
  const filtered     = passes.filter(p =>
    p.studentEmail?.toLowerCase().includes(search.toLowerCase()) ||
    p.code?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="adm-tab-content">
      <div className="adm-passes-toolbar">
        <input className="adm-input adm-search" placeholder="Search email or code…"
          value={search} onChange={e => setSearch(e.target.value)} />
        <button className="adm-btn-primary" onClick={() => setShowForm(v => !v)}>
          {showForm ? "Cancel" : "+ New Pass"}
        </button>
      </div>

      {showForm && (
        <div className="adm-create-form">
          <h3 className="adm-form-title">Create New Pass</h3>
          <div className="adm-form-grid">
            <label className="adm-form-label">Student Email
              <input className="adm-input" value={form.studentEmail}
                onChange={e => set("studentEmail", e.target.value)} placeholder="student@email.com" />
            </label>
            <label className="adm-form-label">Pass Type
              <select className="adm-input" value={form.type} onChange={e => set("type", e.target.value)}>
                {PASS_TYPES.map(t => <option key={t} value={t}>{PASS_LABELS[t]}</option>)}
              </select>
            </label>
            {!isMembership && !form.type.startsWith("private") && (
              <label className="adm-form-label">Classes Total
                <input className="adm-input" type="number" min="1"
                  value={form.classesTotal} onChange={e => set("classesTotal", e.target.value)} />
              </label>
            )}
            <label className="adm-form-label">Expires (optional)
              <input className="adm-input" type="date" value={form.expiresAt}
                onChange={e => set("expiresAt", e.target.value)} />
            </label>
            <label className="adm-form-label" style={{ gridColumn: "1/-1" }}>Notes
              <input className="adm-input" value={form.notes}
                onChange={e => set("notes", e.target.value)} placeholder="e.g. Paid in person" />
            </label>
          </div>
          {formError && <p className="adm-error">{formError}</p>}
          <div className="adm-form-actions">
            <button className="adm-btn-primary" disabled={saving} onClick={handleCreate}>
              {saving ? "Creating…" : "Create Pass"}
            </button>
          </div>
        </div>
      )}

      {loading && <p className="adm-loading">Loading…</p>}
      {!loading && (
        <div className="adm-table-wrap">
          <table className="adm-table">
            <thead>
              <tr><th>Code</th><th>Student</th><th>Type</th><th>Remaining</th><th>Expires</th><th>Status</th><th></th></tr>
            </thead>
            <tbody>
              {filtered.map(p => (
                <tr key={p._id} className={!p.active ? "row-cancelled" : ""}>
                  <td><span className="adm-pass-code-sm">{p.code}</span></td>
                  <td className="adm-email">{p.studentEmail}</td>
                  <td><span className="adm-type-badge">{PASS_LABELS[p.type] ?? p.type}</span></td>
                  <td>{p.classesTotal != null ? `${p.classesTotal - p.classesUsed} / ${p.classesTotal}` : "∞"}</td>
                  <td>{p.expiresAt ? new Date(p.expiresAt).toLocaleDateString() : "—"}</td>
                  <td><span className={`adm-status-badge ${p.active ? "status-confirmed" : "status-cancelled"}`}>{p.active ? "Active" : "Inactive"}</span></td>
                  <td>
                    <button className={p.active ? "adm-btn-danger-sm" : "adm-btn-ghost-sm"} onClick={() => toggleActive(p._id, p.active)}>
                      {p.active ? "Deactivate" : "Reactivate"}
                    </button>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && <tr><td colSpan={7} className="adm-empty">No passes found.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
function CheckInLinkGenerator() {
  const [classes, setClasses] = useState([]);
  const [classId, setClassId] = useState("");
  const [date, setDate]       = useState(new Date().toISOString().slice(0,10));
  const link = classId ? `${window.location.origin}/checkin?classId=${classId}&date=${date}` : null;

  useEffect(() => {
    fetch(`${API}/schedule/classes`, { headers: { Authorization: ADMIN_SECRET } })
      .then(r => r.json())
      .then(d => setClasses(d.classes ?? []));
  }, []);

  return (
    <div style={{display:"flex", flexDirection:"column", gap:"1rem", maxWidth:"480px"}}>
      <div className="adm-field-group">
        <label className="adm-label">Class</label>
        <select className="adm-input" value={classId} onChange={e => setClassId(e.target.value)}>
          <option value="">— select a class —</option>
          {classes.map(c => (
            <option key={c.id} value={c.id}>{c.title} — {c.dayOfWeek} {c.time}</option>
          ))}
        </select>
      </div>
      <div className="adm-field-group">
        <label className="adm-label">Date</label>
        <input className="adm-input" type="date" value={date} onChange={e => setDate(e.target.value)} />
      </div>
      {link && (
        <div style={{background:"var(--color-grey-100)", border:"1px solid var(--color-grey-200)",
          borderRadius:"8px", padding:"1rem"}}>
          <p style={{fontSize:"0.8rem", color:"var(--color-grey-400)", marginBottom:"0.5rem"}}>Kiosk link:</p>
          <a href={link} target="_blank" rel="noopener noreferrer"
            style={{fontSize:"0.85rem", color:"var(--color-tertiary)", wordBreak:"break-all"}}>
            {link}
          </a>
          <button className="adm-btn-sm" style={{marginTop:"0.75rem", display:"block"}}
            onClick={() => navigator.clipboard.writeText(link)}>
            Copy link
          </button>
        </div>
      )}
    </div>
  );
}

// ── Check-in Tab ──────────────────────────────────────────
function CheckInTab() {
  return (
    <div className="adm-tab-content">
      <p style={{marginBottom:"1rem", color:"var(--color-grey-400)", fontSize:"0.9rem"}}>
        Open the check-in screen on an iPad or phone at the studio entrance.
        Students tap their name to check in.
      </p>
      <p style={{marginBottom:"0.5rem", fontSize:"0.85rem", color:"var(--color-grey-400)"}}>
        Select a class and date to generate a kiosk link:
      </p>
      <CheckInLinkGenerator />
    </div>
  );
}
// ── Calendar Tab ──────────────────────────────────────────
function CalendarTab() {
  return <div className="adm-tab-content adm-calendar-tab"><YogaCalendar initialAdminMode={true} /></div>;
}

// ── Reports Tab ───────────────────────────────────────────
const REPORT_OPTIONS = [
  { id: "checkin",         label: "R1 — Check-in" },
  { id: "first-visits",   label: "R2 — First Visits" },
  { id: "payroll",        label: "R4 — Payroll" },
  { id: "monthly-revenue",label: "R5 — Monthly Revenue" },
  { id: "workshop-revenue",label: "R6 — Workshop Revenue" },
  { id: "attendance",     label: "Attendance (legacy)" },
  { id: "revenue",        label: "Revenue (legacy)" },
  { id: "pass-alerts",    label: "Pass Alerts" },
];

function ExportBar({ onXLSX, disabled }) {
  return (
    <div className="adm-export-bar">
      <button className="adm-btn-export" onClick={onXLSX} disabled={disabled}>
        ↓ Export XLSX
      </button>
    </div>
  );
}

// R1 — Check-in ────────────────────────────────────────────
function CheckinReport({ token }) {
  const today = toLocalDateStr(new Date());
  const [from, setFrom]     = useState(() => { const d = new Date(); d.setDate(d.getDate() - 13); return toLocalDateStr(d); });
  const [to, setTo]         = useState(today);
  const [data, setData]     = useState(null);
  const [loading, setLoading] = useState(false);

  const fetch_ = useCallback(async () => {
    setLoading(true);
    try {
      const res  = await fetch(`${API}/reports/checkin?from=${from}&to=${to}`, { headers: { Authorization: `Bearer ${token}` } });
      const json = await res.json();
      setData(json);
    } catch { setData(null); }
    finally   { setLoading(false); }
  }, [from, to, token]);

  useEffect(() => { fetch_(); }, [fetch_]);

  const doExport = () => {
    if (!data?.classes) return;
    const rows = data.classes.map(c => ({
      Date: c.date, Class: c.title, Teacher: c.teacher,
      Booked: c.booked, "Checked In": c.checkedIn, "No Show": c.noShow,
      "Check-in Rate %": c.checkinRate,
    }));
    rows.push({ Date: "TOTAL", Class: "", Teacher: "",
      Booked: data.totals.booked, "Checked In": data.totals.checkedIn,
      "No Show": data.totals.noShow, "Check-in Rate %": data.totals.checkinRate });
    exportXLSX("Check-in", rows, `studioflow-checkin-${from}-${to}.xlsx`);
  };

  return (
    <div>
      <div className="adm-report-filters">
        <div className="adm-filter-group">
          <label className="adm-filter-label">From</label>
          <input type="date" className="adm-input" value={from} onChange={e => setFrom(e.target.value)} />
        </div>
        <div className="adm-filter-group">
          <label className="adm-filter-label">To</label>
          <input type="date" className="adm-input" value={to} max={today} onChange={e => setTo(e.target.value)} />
        </div>
      </div>

      {loading && <p className="adm-loading">Loading…</p>}
      {data && !loading && (
        <>
          <div className="adm-report-summary-row">
            {[
              { label: "Classes", value: data.classes.length },
              { label: "Total booked", value: data.totals.booked },
              { label: "Checked in", value: data.totals.checkedIn },
              { label: "No shows", value: data.totals.noShow },
              { label: "Check-in rate", value: `${data.totals.checkinRate}%` },
            ].map(s => (
              <div key={s.label} className="adm-summary-chip">
                <span className="adm-summary-val">{s.value}</span>
                <span className="adm-summary-lbl">{s.label}</span>
              </div>
            ))}
          </div>

          <ExportBar onXLSX={doExport} />

          <div className="adm-table-wrap">
            <table className="adm-table">
              <thead>
                <tr><th>Date</th><th>Class</th><th>Teacher</th><th>Booked</th><th>Checked In</th><th>No Show</th><th>Rate</th></tr>
              </thead>
              <tbody>
                {data.classes.map((c, i) => (
                  <tr key={i}>
                    <td>{c.date}</td><td>{c.title}</td><td>{c.teacher}</td>
                    <td>{c.booked}</td>
                    <td><span className="adm-checkin-count">{c.checkedIn}</span></td>
                    <td className={c.noShow > 0 ? "adm-noshow" : ""}>{c.noShow}</td>
                    <td>
                      <div className="adm-rate-bar">
                        <div className="adm-rate-fill" style={{ width: `${c.checkinRate}%` }} />
                        <span>{c.checkinRate}%</span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

// R2 — First Visits ────────────────────────────────────────
function FirstVisitsReport({ token }) {
  const today = toLocalDateStr(new Date());
  const [from, setFrom]   = useState(() => { const d = new Date(); d.setDate(1); return toLocalDateStr(d); });
  const [to, setTo]       = useState(today);
  const [data, setData]   = useState(null);
  const [loading, setLoading] = useState(false);

  const fetch_ = useCallback(async () => {
    setLoading(true);
    try {
      const res  = await fetch(`${API}/reports/first-visits?from=${from}&to=${to}`, { headers: { Authorization: `Bearer ${token}` } });
      const json = await res.json();
      setData(json);
    } catch { setData(null); }
    finally   { setLoading(false); }
  }, [from, to, token]);

  useEffect(() => { fetch_(); }, [fetch_]);

  const doExport = () => {
    if (!data?.visitors) return;
    exportXLSX("First Visits", data.visitors.map(v => ({
      Name: v.name, Email: v.email,
      "First Visit": fmtDate(v.firstVisit),
      State: v.state ?? "—", Zip: v.zip ?? "—",
      County: v.county ?? "—",
      Local: v.isLocal ? "Yes" : "No",
      "Out of State": v.outOfState ? "Yes" : "No",
      "Payment Type": v.paymentLabel,
      "Converted to Pass": v.converted ? "Yes" : "No",
    })), `studioflow-first-visits-${from}-${to}.xlsx`);
  };

  return (
    <div>
      <div className="adm-report-filters">
        <div className="adm-filter-group">
          <label className="adm-filter-label">From</label>
          <input type="date" className="adm-input" value={from} onChange={e => setFrom(e.target.value)} />
        </div>
        <div className="adm-filter-group">
          <label className="adm-filter-label">To</label>
          <input type="date" className="adm-input" value={to} max={today} onChange={e => setTo(e.target.value)} />
        </div>
      </div>

      {loading && <p className="adm-loading">Loading…</p>}
      {data && !loading && (
        <>
          <div className="adm-report-summary-row">
            {[
              { label: "New students",  value: data.summary.total },
              { label: "Local (Tampa Bay)", value: data.summary.local },
              { label: "Out of state",  value: data.summary.outOfState },
              { label: "Other FL",      value: data.summary.otherFL },
              { label: "Drop-in",       value: data.summary.dropIns },
              { label: "Converted",     value: data.summary.converted },
            ].map(s => (
              <div key={s.label} className="adm-summary-chip">
                <span className="adm-summary-val">{s.value}</span>
                <span className="adm-summary-lbl">{s.label}</span>
              </div>
            ))}
          </div>

          <ExportBar onXLSX={doExport} />

          {data.visitors.length === 0
            ? <p className="adm-empty">No new students in this period.</p>
            : (
              <div className="adm-table-wrap">
                <table className="adm-table">
                  <thead>
                    <tr><th>Name</th><th>First Visit</th><th>Location</th><th>Payment</th><th>Local</th><th>Converted</th></tr>
                  </thead>
                  <tbody>
                    {data.visitors.map((v, i) => (
                      <tr key={i}>
                        <td>
                          <div>{v.name}</div>
                          <div className="adm-email">{v.email}</div>
                        </td>
                        <td>{fmtDate(v.firstVisit)}</td>
                        <td>
                          {v.zip && <span>{v.zip}</span>}
                          {v.county && <span className="adm-locality-badge">{v.county}</span>}
                          {v.outOfState && <span className="adm-oos-badge">{v.state}</span>}
                        </td>
                        <td><span className="adm-type-badge">{v.paymentLabel}</span></td>
                        <td>{v.isLocal ? <span className="adm-local-yes">✓ Local</span> : <span className="adm-local-no">—</span>}</td>
                        <td>{v.converted ? <span className="adm-converted-yes">✓</span> : "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          }
        </>
      )}
    </div>
  );
}

// R4 — Payroll ─────────────────────────────────────────────
function PayrollReport({ token }) {
  const today = toLocalDateStr(new Date());
  const [from, setFrom]   = useState(() => { const d = new Date(); d.setDate(1); return toLocalDateStr(d); });
  const [to, setTo]       = useState(today);
  const [data, setData]   = useState(null);
  const [loading, setLoading] = useState(false);

  const fetch_ = useCallback(async () => {
    setLoading(true);
    try {
      const res  = await fetch(`${API}/reports/payroll?from=${from}&to=${to}`, { headers: { Authorization: `Bearer ${token}` } });
      const json = await res.json();
      setData(json);
    } catch { setData(null); }
    finally   { setLoading(false); }
  }, [from, to, token]);

  useEffect(() => { fetch_(); }, [fetch_]);

  const doExport = () => {
    if (!data?.teachers) return;
    const rows = [];
    for (const t of data.teachers) {
      const rate = STAFF_MAP[t.teacher] ?? 0;
      for (const c of t.classes) {
        rows.push({
          Teacher: t.teacher, Date: c.date, Class: c.title,
          "Pay Rate": `$${rate}/student`,
          Booked: c.booked, Attended: c.attended,
          Pay: `$${(c.attended * rate).toFixed(2)}`,
        });
      }
      rows.push({ Teacher: t.teacher, Date: "SUBTOTAL", Class: "",
        Booked: t.classes.reduce((s, c) => s + c.booked, 0),
        Attended: t.classes.reduce((s, c) => s + c.attended, 0),
        Pay: `$${t.classes.reduce((s, c) => s + c.attended * (STAFF_MAP[t.teacher] ?? 0), 0).toFixed(2)}`,
      });
    }
    exportXLSX("Payroll", rows, `studioflow-payroll-${from}-${to}.xlsx`);
  };

  return (
    <div>
      <div className="adm-report-filters">
        <div className="adm-filter-group">
          <label className="adm-filter-label">From</label>
          <input type="date" className="adm-input" value={from} onChange={e => setFrom(e.target.value)} />
        </div>
        <div className="adm-filter-group">
          <label className="adm-filter-label">To</label>
          <input type="date" className="adm-input" value={to} max={today} onChange={e => setTo(e.target.value)} />
        </div>
      </div>

      {loading && <p className="adm-loading">Loading…</p>}
      {data && !loading && (
        <>
          <ExportBar onXLSX={doExport} />
          {(!data.teachers || data.teachers.length === 0)
            ? <p className="adm-empty">No classes in this period.</p>
            : data.teachers.map(t => {
                const rate         = STAFF_MAP[t.teacher] ?? 0;
                const totalAttended = t.classes.reduce((s, c) => s + c.attended, 0);
                const totalPay     = t.classes.reduce((s, c) => s + c.attended * rate, 0);
                return (
                  <div key={t.teacher} className="adm-report-teacher">
                    <div className="adm-report-teacher-header">
                      <span className="adm-report-teacher-name">{t.teacher}</span>
                      <span className="adm-report-teacher-rate">${rate}/student attended</span>
                    </div>
                    <table className="adm-report-table">
                      <thead>
                        <tr><th>Date</th><th>Class</th><th>Booked</th><th>Attended</th><th>Pay</th></tr>
                      </thead>
                      <tbody>
                        {t.classes.map((c, i) => (
                          <tr key={i}>
                            <td>{c.date}</td><td>{c.title}</td><td>{c.booked}</td>
                            <td><strong>{c.attended}</strong></td>
                            <td>{fmtMoney(c.attended * rate)}</td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr className="adm-report-subtotal">
                          <td colSpan={3}>Subtotal</td>
                          <td><strong>{totalAttended}</strong></td>
                          <td>{fmtMoney(totalPay)}</td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                );
              })
          }
        </>
      )}
    </div>
  );
}

// R5 — Monthly Revenue ─────────────────────────────────────
function MonthlyRevenueReport({ token }) {
  const [month, setMonth] = useState(currentMonth());
  const [data, setData]   = useState(null);
  const [loading, setLoading] = useState(false);

  const fetch_ = useCallback(async () => {
    setLoading(true);
    try {
      const res  = await fetch(`${API}/reports/monthly-revenue?month=${month}`, { headers: { Authorization: `Bearer ${token}` } });
      const json = await res.json();
      setData(json);
    } catch { setData(null); }
    finally   { setLoading(false); }
  }, [month, token]);

  useEffect(() => { fetch_(); }, [fetch_]);

  const doExport = () => {
    if (!data) return;
    const rows = [];
    rows.push({ Category: "Drop-in Classes", Type: "Drop-in", Count: data.dropins.count, Total: data.dropins.total, Notes: "Collected" });
    for (const [type, d] of Object.entries(data.passes ?? {})) {
      rows.push({ Category: "Pass Sales", Type: PASS_LABELS[type] ?? type, Count: d.count, Total: d.total, Notes: "Collected" });
    }
    for (const [type, d] of Object.entries(data.memberships?.collected ?? {})) {
      rows.push({ Category: "Memberships", Type: PASS_LABELS[type] ?? type, Count: d.count, Total: d.total, Notes: "Collected" });
    }
    for (const [type, d] of Object.entries(data.memberships?.projected ?? {})) {
      rows.push({ Category: "Memberships", Type: PASS_LABELS[type] ?? type, Count: d.count, Total: d.total, Notes: "Projected" });
    }
    rows.push({ Category: "TOTAL COLLECTED", Type: "", Count: "", Total: data.totals.collected, Notes: "" });
    rows.push({ Category: "PROJECTED", Type: "", Count: "", Total: data.totals.projected, Notes: "Not yet received" });
    rows.push({ Category: "FORECAST", Type: "", Count: "", Total: data.totals.forecast, Notes: "Collected + Projected" });
    exportXLSX("Monthly Revenue", rows, `studioflow-revenue-${month}.xlsx`);
  };

  return (
    <div>
      <div className="adm-report-filters">
        <div className="adm-filter-group">
          <label className="adm-filter-label">Month</label>
          <input type="month" className="adm-input" value={month} onChange={e => setMonth(e.target.value)} />
        </div>
      </div>

      {loading && <p className="adm-loading">Loading…</p>}
      {data && !loading && (
        <>
          <div className="adm-report-summary-row">
            <div className="adm-summary-chip adm-summary-chip--green">
              <span className="adm-summary-val">{fmtMoney(data.totals.collected)}</span>
              <span className="adm-summary-lbl">Collected</span>
            </div>
            <div className="adm-summary-chip adm-summary-chip--amber">
              <span className="adm-summary-val">{fmtMoney(data.totals.projected)}</span>
              <span className="adm-summary-lbl">Projected</span>
            </div>
            <div className="adm-summary-chip adm-summary-chip--blue">
              <span className="adm-summary-val">{fmtMoney(data.totals.forecast)}</span>
              <span className="adm-summary-lbl">Month forecast</span>
            </div>
          </div>

          <ExportBar onXLSX={doExport} />

          <h4 className="adm-report-section-title">Drop-in Classes</h4>
          <table className="adm-report-table">
            <thead><tr><th>Type</th><th>Count</th><th>Total</th></tr></thead>
            <tbody>
              <tr><td>Drop-in classes</td><td>{data.dropins.count}</td><td>{fmtMoney(data.dropins.total)}</td></tr>
            </tbody>
          </table>

          <h4 className="adm-report-section-title">Pass Sales</h4>
          <table className="adm-report-table">
            <thead><tr><th>Pass Type</th><th>Sold</th><th>Revenue</th></tr></thead>
            <tbody>
              {Object.entries(data.passes ?? {}).length === 0
                ? <tr><td colSpan={3} className="adm-empty">No passes sold.</td></tr>
                : Object.entries(data.passes).map(([type, d]) => (
                    <tr key={type}><td>{PASS_LABELS[type] ?? type}</td><td>{d.count}</td><td>{fmtMoney(d.total)}</td></tr>
                  ))
              }
            </tbody>
          </table>

          <h4 className="adm-report-section-title">Memberships — Collected</h4>
          <table className="adm-report-table">
            <thead><tr><th>Type</th><th>Count</th><th>Revenue</th></tr></thead>
            <tbody>
              {Object.entries(data.memberships?.collected ?? {}).length === 0
                ? <tr><td colSpan={3} className="adm-empty">No memberships collected.</td></tr>
                : Object.entries(data.memberships.collected).map(([type, d]) => (
                    <tr key={type}><td>{PASS_LABELS[type] ?? type}</td><td>{d.count}</td><td>{fmtMoney(d.total)}</td></tr>
                  ))
              }
            </tbody>
          </table>

          {Object.keys(data.memberships?.projected ?? {}).length > 0 && (
            <>
              <h4 className="adm-report-section-title">Memberships — Projected (renewing this month)</h4>
              <table className="adm-report-table">
                <thead><tr><th>Type</th><th>Count</th><th>Expected</th></tr></thead>
                <tbody>
                  {Object.entries(data.memberships.projected).map(([type, d]) => (
                    <tr key={type} className="adm-projected-row">
                      <td>{PASS_LABELS[type] ?? type}</td><td>{d.count}</td><td>{fmtMoney(d.total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}

          <table className="adm-report-table adm-totals-table">
            <tbody>
              <tr className="adm-report-subtotal"><td>Total collected</td><td></td><td>{fmtMoney(data.totals.collected)}</td></tr>
              <tr className="adm-projected-row"><td>Projected (pending)</td><td></td><td>{fmtMoney(data.totals.projected)}</td></tr>
              <tr className="adm-forecast-row"><td>Month forecast</td><td></td><td>{fmtMoney(data.totals.forecast)}</td></tr>
            </tbody>
          </table>
        </>
      )}
    </div>
  );
}

// R6 — Workshop Revenue ────────────────────────────────────
function WorkshopRevenueReport({ token }) {
  const today = toLocalDateStr(new Date());
  const [from, setFrom]   = useState(() => { const d = new Date(); d.setDate(1); return toLocalDateStr(d); });
  const [to, setTo]       = useState(today);
  const [data, setData]   = useState(null);
  const [loading, setLoading] = useState(false);

  const fetch_ = useCallback(async () => {
    setLoading(true);
    try {
      const res  = await fetch(`${API}/reports/workshop-revenue?from=${from}&to=${to}`, { headers: { Authorization: `Bearer ${token}` } });
      const json = await res.json();
      setData(json);
    } catch { setData(null); }
    finally   { setLoading(false); }
  }, [from, to, token]);

  useEffect(() => { fetch_(); }, [fetch_]);

  const doExport = () => {
    if (!data?.workshops) return;
    const rows = [];
    for (const w of data.workshops) {
      for (const s of w.sessions) {
        rows.push({ Workshop: w.title, Session: s.label, Time: s.time, Bookings: s.bookings });
      }
      rows.push({ Workshop: w.title, Session: "TOTAL", Bookings: w.bookings });
    }
    exportXLSX("Workshop Revenue", rows, `studioflow-workshops-${from}-${to}.xlsx`);
  };

  return (
    <div>
      <div className="adm-report-filters">
        <div className="adm-filter-group">
          <label className="adm-filter-label">From</label>
          <input type="date" className="adm-input" value={from} onChange={e => setFrom(e.target.value)} />
        </div>
        <div className="adm-filter-group">
          <label className="adm-filter-label">To</label>
          <input type="date" className="adm-input" value={to} max={today} onChange={e => setTo(e.target.value)} />
        </div>
      </div>

      {loading && <p className="adm-loading">Loading…</p>}
      {data && !loading && (
        <>
          <ExportBar onXLSX={doExport} />
          {data.workshops.length === 0
            ? <p className="adm-empty">No workshops in this period.</p>
            : data.workshops.map((w, i) => (
                <div key={i} className="adm-report-teacher">
                  <div className="adm-report-teacher-header">
                    <span className="adm-report-teacher-name">{w.title}</span>
                    <span className="adm-report-teacher-rate">{w.bookings} bookings</span>
                  </div>
                  <table className="adm-report-table">
                    <thead><tr><th>Session</th><th>Time</th><th>Bookings</th></tr></thead>
                    <tbody>
                      {w.sessions.map((s, j) => (
                        <tr key={j}><td>{s.label}</td><td>{s.time}</td><td>{s.bookings}</td></tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="adm-report-subtotal"><td colSpan={2}>Total bookings</td><td>{w.bookings}</td></tr>
                    </tfoot>
                  </table>
                </div>
              ))
          }
        </>
      )}
    </div>
  );
}

// Legacy Attendance ────────────────────────────────────────
function LegacyAttendanceReport({ token }) {
  const today  = toLocalDateStr(new Date());
  const [rangeEnd, setRangeEnd] = useState(today);
  const [data, setData]         = useState([]);
  const [loading, setLoading]   = useState(false);

  const rangeStart = (() => { const d = new Date(`${rangeEnd}T12:00:00`); d.setDate(d.getDate() - 13); return toLocalDateStr(d); })();
  const shiftRange = dir => { const d = new Date(`${rangeEnd}T12:00:00`); d.setDate(d.getDate() + dir * 14); setRangeEnd(toLocalDateStr(d)); };

  useEffect(() => {
    setLoading(true);
    fetch(`${API}/reports/attendance?from=${rangeStart}&to=${rangeEnd}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json()).then(d => setData(d.classes ?? [])).catch(() => setData([])).finally(() => setLoading(false));
  }, [rangeEnd, token]);

  const byTeacher = {};
  for (const c of data) {
    const t = c.teacher || "Unknown";
    if (!byTeacher[t]) byTeacher[t] = [];
    byTeacher[t].push(c);
  }

  const doExport = () => {
    const rows = data.map(c => ({ Date: c.date, Class: c.title, Teacher: c.teacher, Confirmed: c.confirmed, Cancelled: c.cancelled, Pay: (c.confirmed * (STAFF_MAP[c.teacher] ?? 0)).toFixed(2) }));
    exportXLSX("Attendance", rows, `studioflow-attendance-${rangeStart}-${rangeEnd}.xlsx`);
  };

  return (
    <div>
      <div className="adm-report-range">
        <button onClick={() => shiftRange(-1)}>← Prev</button>
        <span className="adm-range-label">{rangeStart} – {rangeEnd}</span>
        <button onClick={() => shiftRange(1)} disabled={rangeEnd >= today}>Next →</button>
      </div>
      {loading && <p className="adm-loading">Loading…</p>}
      {!loading && <ExportBar onXLSX={doExport} disabled={data.length === 0} />}
      {Object.entries(byTeacher).map(([teacher, classes]) => {
        const rate = STAFF_MAP[teacher] ?? 0;
        const totalConfirmed = classes.reduce((s, c) => s + c.confirmed, 0);
        const totalPay = classes.reduce((s, c) => s + c.confirmed * rate, 0);
        return (
          <div key={teacher} className="adm-report-teacher">
            <div className="adm-report-teacher-header">
              <span className="adm-report-teacher-name">{teacher}</span>
              <span className="adm-report-teacher-rate">${rate}/student</span>
            </div>
            <table className="adm-report-table">
              <thead><tr><th>Date</th><th>Class</th><th>Confirmed</th><th>Cancelled</th><th>Pay</th></tr></thead>
              <tbody>
                {classes.map((c, i) => (
                  <tr key={i}>
                    <td>{c.date}</td><td>{c.title}</td><td>{c.confirmed}</td>
                    <td className="adm-report-cancelled">{c.cancelled || 0}</td>
                    <td>{fmtMoney(c.confirmed * rate)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="adm-report-subtotal"><td colSpan={2}>Subtotal</td><td>{totalConfirmed}</td><td></td><td>{fmtMoney(totalPay)}</td></tr>
              </tfoot>
            </table>
          </div>
        );
      })}
    </div>
  );
}

// Legacy Revenue + Pass Alerts (combined) ──────────────────
function LegacyRevenueReport({ token }) {
  const today  = toLocalDateStr(new Date());
  const [rangeEnd, setRangeEnd] = useState(today);
  const [revenue, setRevenue]   = useState(null);
  const [alerts, setAlerts]     = useState([]);
  const [loading, setLoading]   = useState(false);
  const [reminderState, setReminderState] = useState("idle");

  const rangeStart = (() => { const d = new Date(`${rangeEnd}T12:00:00`); d.setDate(d.getDate() - 13); return toLocalDateStr(d); })();
  const shiftRange = dir => { const d = new Date(`${rangeEnd}T12:00:00`); d.setDate(d.getDate() + dir * 14); setRangeEnd(toLocalDateStr(d)); };

  useEffect(() => {
    setLoading(true);
    Promise.all([
      fetch(`${API}/reports/revenue?from=${rangeStart}&to=${rangeEnd}`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()),
      fetch(`${API}/reports/pass-alerts`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()),
    ]).then(([rev, al]) => { setRevenue(rev); setAlerts(al.alerts ?? []); })
      .catch(() => {}).finally(() => setLoading(false));
  }, [rangeEnd, token]);

  const handleSendReminders = async () => {
    setReminderState("sending");
    try {
      await fetch(`${API}/reports/pass-alerts/remind-all`, {
        method: "POST", headers: AUTH, body: JSON.stringify({ alerts }),
      });
      setReminderState("sent");
    } catch { setReminderState("idle"); }
  };

  const doExport = () => {
    if (!revenue) return;
    const rows = [
      { Type: "Drop-in classes", Count: revenue.dropins.count, Total: revenue.dropins.total },
      ...Object.entries(revenue.passes ?? {}).map(([t, d]) => ({ Type: PASS_LABELS[t] ?? t, Count: d.count, Total: d.total })),
      { Type: "GRAND TOTAL", Count: "", Total: revenue.grandTotal },
    ];
    exportXLSX("Revenue", rows, `studioflow-revenue-${rangeStart}-${rangeEnd}.xlsx`);
  };

  return (
    <div>
      <div className="adm-report-range">
        <button onClick={() => shiftRange(-1)}>← Prev</button>
        <span className="adm-range-label">{rangeStart} – {rangeEnd}</span>
        <button onClick={() => shiftRange(1)} disabled={rangeEnd >= today}>Next →</button>
      </div>
      {loading && <p className="adm-loading">Loading…</p>}
      {revenue && !loading && (
        <>
          <ExportBar onXLSX={doExport} />
          <table className="adm-report-table">
            <thead><tr><th>Type</th><th>Count</th><th>Total</th></tr></thead>
            <tbody>
              <tr><td>Drop-in classes</td><td>{revenue.dropins.count}</td><td>{fmtMoney(revenue.dropins.total)}</td></tr>
              {Object.entries(revenue.passes ?? {}).map(([t, d]) => (
                <tr key={t}><td>{PASS_LABELS[t] ?? t}</td><td>{d.count}</td><td>{fmtMoney(d.total)}</td></tr>
              ))}
            </tbody>
            <tfoot><tr className="adm-report-subtotal"><td colSpan={2}>Total</td><td>{fmtMoney(revenue.grandTotal)}</td></tr></tfoot>
          </table>

          <h4 className="adm-report-section-title" style={{ marginTop: "2rem" }}>Pass Alerts</h4>
          {alerts.length === 0
            ? <p className="adm-empty">No passes expiring soon or running low.</p>
            : (
              <>
                {reminderState === "idle" && <button className="adm-btn adm-btn--remind" onClick={() => setReminderState("confirm")}>Send reminders to {alerts.length} student{alerts.length !== 1 ? "s" : ""}</button>}
                {reminderState === "confirm" && <div className="adm-remind-confirm"><span>Send to {alerts.length} student{alerts.length !== 1 ? "s" : ""}?</span><button className="adm-btn adm-btn--primary" onClick={handleSendReminders}>Yes, send</button><button className="adm-btn" onClick={() => setReminderState("idle")}>Cancel</button></div>}
                {reminderState === "sending" && <p className="adm-loading">Sending…</p>}
                {reminderState === "sent"    && <p className="adm-remind-sent">✓ Reminders sent to {alerts.length} student{alerts.length !== 1 ? "s" : ""}</p>}
                <table className="adm-report-table adm-alerts-table">
                  <thead><tr><th>Student</th><th>Pass</th><th>Remaining</th><th>Expires</th><th>Alert</th></tr></thead>
                  <tbody>
                    {alerts.map((a, i) => (
                      <tr key={i}><td>{a.studentEmail}</td><td>{a.passLabel}</td><td>{a.classesRemaining}</td>
                        <td>{a.expiresAt ? fmtDate(a.expiresAt) : "—"}</td><td>{a.reasons.join(" · ")}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )
          }
        </>
      )}
    </div>
  );
}

// ── Reports Tab container ─────────────────────────────────
function ReportsTab({ token }) {
  const [activeReport, setActiveReport] = useState("checkin");

  const reportComponent = () => {
    switch (activeReport) {
      case "checkin":          return <CheckinReport token={token} />;
      case "first-visits":    return <FirstVisitsReport token={token} />;
      case "payroll":         return <PayrollReport token={token} />;
      case "monthly-revenue": return <MonthlyRevenueReport token={token} />;
      case "workshop-revenue":return <WorkshopRevenueReport token={token} />;
      case "attendance":      return <LegacyAttendanceReport token={token} />;
      case "revenue":         return <LegacyRevenueReport token={token} />;
      case "pass-alerts":     return <LegacyRevenueReport token={token} />;
      default:                return null;
    }
  };

  return (
    <div className="adm-tab-content adm-reports">
      <div className="adm-report-selector">
        <label className="adm-filter-label">Report</label>
        <select className="adm-input adm-report-dropdown" value={activeReport} onChange={e => setActiveReport(e.target.value)}>
          <optgroup label="Reports">
            {REPORT_OPTIONS.slice(0, 5).map(r => <option key={r.id} value={r.id}>{r.label}</option>)}
          </optgroup>
          <optgroup label="Legacy">
            {REPORT_OPTIONS.slice(5).map(r => <option key={r.id} value={r.id}>{r.label}</option>)}
          </optgroup>
        </select>
      </div>

      <div className="adm-report-body">
        {reportComponent()}
      </div>
    </div>
  );
}

// ── Main Admin Page ───────────────────────────────────────
export default function Admin() {
  const [authed, setAuthed] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get("admin") === "true";
  });
  const [tab, setTab] = useState("bookings");

  if (!authed) return <PasswordGate onSuccess={() => setAuthed(true)} />;

  return (
    <div className="adm-wrap">
      <div className="adm-header">
        <div className="adm-header-left">
          <span className="adm-header-logo">⚙</span>
          <div>
            <h1 className="adm-header-title">Admin Dashboard</h1>
            <p className="adm-header-sub">Iyengar Yoga St. Petersburg</p>
          </div>
        </div>
        <a href="/" className="adm-back-link">← Back to site</a>
      </div>

      <div className="adm-tabs">
        {[
          { id: "bookings", label: "Bookings" },
          { id: "students", label: "Students" },
          { id: "passes",   label: "Passes" },
          { id: "calendar", label: "Calendar" },
          { id: "reports",  label: "Reports" },
          { id: "checkin",  label: "Check-in" },
        ].map(t => (
          <button key={t.id} className={`adm-tab ${tab === t.id ? "active" : ""}`} onClick={() => setTab(t.id)}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === "bookings" && <BookingsTab />}
      {tab === "students" && <StudentsTab />}
      {tab === "passes"   && <PassesTab />}
      {tab === "calendar" && <CalendarTab />}
      {tab === "reports"  && <ReportsTab token={ADMIN_SECRET} />}
      {tab === "checkin"  && <CheckInTab token={ADMIN_SECRET} />}
    </div>
  );
}
